import { CheckIcon, UserIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import type { TeacherTestDetails } from "p6m-p6u";
import { cp } from "../../config";
import { personalDataWebsitePath } from "../../constants/RedirectConstants";
import PDFDownloadButton, { PDFDownloadButtonVariants } from "../../basic/pdfBasic/PDFDownloadButton/PDFDownloadButton";
import { AreaButton, ButtonVariant } from "../teacherArea/AreaButton";
import { FormattedText } from "../teacherArea/FormattedText";
import AnonymousFlowPopupFrame from "./AnonymousFlowPopupFrame";
import DecorativeTestSheet from "./DecorativeTestSheet";

const TITLE_ID = "account-created-popup-title";

interface AccountCreatedPopupProps {
    isOpen: boolean;
    onClose: () => void;
    /** Where the password went for an e-mail registration; Google/Apple accounts get none mailed, so it's absent. */
    email?: string;
    /** Undefined when the test content is no longer in memory (e.g. after a reload), which hides the PDF button. */
    testDetails?: TeacherTestDetails;
}

/**
 * Previews the onboarding on /assistent: the same three steps, in the same order and wording (shared
 * Transifex strings), with the first one already ticked off. The continue button leads straight to
 * step 2 of it, the personal-data form on the website.
 */
const AccountCreatedPopup = (props: AccountCreatedPopupProps) => {
    const t = useT();

    const t_title = t("Account created, test saved", { _tags: "AssistantOverview, AccountCreatedPopup" });
    const t_subtitle = t("You can find it in your account at any time and edit it whenever you want.", {
        _tags: "AssistantOverview, AccountCreatedPopup",
    });
    const t_downloadPdf = t("Download PDF", { _tags: "AssistantOverview, AccountCreatedPopup" });
    const t_continue = t("Continue to your next steps", { _tags: "AssistantOverview, AccountCreatedPopup" });
    const t_passwordSent = t("Password sent to <b>{email}</b>", {
        email: props.email,
        _tags: "AssistantOverview, AccountCreatedPopup",
    });
    const t_exercisePDF = t("Exercise Sheet", { _tags: "CreateTest,PDFCreation" });
    const t_test = t("Test", { _tags: "CreateTest,PDFCreation" });
    // Same keys as the onboarding on /assistent, so both always say the same thing.
    const t_createFirstTest = t("Create your first test", { _tags: "AssistantOverview" });
    const t_completeProfile = t("Complete your profile", { _tags: "AssistantOverview" });
    const t_createVocabularyTraining = t("Create your first vocabulary training", { _tags: "AssistantOverview" });

    const testName = props.testDetails?.name ?? t_exercisePDF;
    // Step 2 of the onboarding, so the teacher lands on the form they are asked to fill out next.
    const personalDataUrl = `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}${personalDataWebsitePath}`;

    const renderStepBadge = (step: number, isDone: boolean) => (
        <span
            aria-hidden="true"
            className={`flex items-center justify-center w-[22px] h-[22px] shrink-0 rounded-circle text-xs font-semibold ${
                isDone ? "bg-p6-green text-white" : "border-2 border-p6-grey-light text-p6-grey-mid"
            }`}
        >
            {isDone ? (
                <CheckIcon
                    size={14}
                    weight="bold"
                />
            ) : (
                step
            )}
        </span>
    );

    const renderStep = (step: number, isDone: boolean, title: string) => (
        <li className="flex items-center gap-xs min-h-[48px] px-xxs">
            {renderStepBadge(step, isDone)}
            <span
                className={`flex-1 min-w-[0] text-m ${
                    isDone ? "text-p6-grey-mid line-through" : "text-p6-grey-dark font-semibold"
                }`}
            >
                {title}
            </span>
            {step === 1 && props.testDetails && (
                <div className="shrink-0">
                    <PDFDownloadButton
                        variant={PDFDownloadButtonVariants.TEXT_LINK}
                        buttonText={t_downloadPdf}
                        fileName={`${testName} ${t_test}.pdf`}
                        testDetails={props.testDetails}
                        analyticsSource="account-created-popup"
                    />
                </div>
            )}
        </li>
    );

    return (
        <AnonymousFlowPopupFrame
            isOpen={props.isOpen}
            onClose={props.onClose}
            titleId={TITLE_ID}
            data-testid="account-created-popup"
        >
            <div className="flex flex-col items-center gap-xs text-center px-s">
                <div
                    aria-hidden="true"
                    className="relative flex items-end justify-center w-[100px] h-[124px] mb-xs"
                >
                    <DecorativeTestSheet name={testName} />
                    <span className="absolute -right-[2px] -bottom-[4px] flex items-center justify-center w-[28px] h-[28px] rounded-circle bg-p6-orange text-white border-[3px] border-white box-content">
                        <UserIcon size={16} />
                    </span>
                </div>
                <h2
                    id={TITLE_ID}
                    className="text-xl font-semibold text-p6-grey-dark leading-tight"
                >
                    {t_title}
                </h2>
                <p className="text-l text-p6-grey-dark">{t_subtitle}</p>
            </div>

            <ol className="flex flex-col list-none">
                {renderStep(1, true, t_createFirstTest)}
                {renderStep(2, false, t_completeProfile)}
                {renderStep(3, false, t_createVocabularyTraining)}
            </ol>

            <div className="flex flex-col gap-xs">
                {/* Leaving counts as closing: the router state would otherwise bring the popup back on Back. */}
                <AreaButton
                    data-testid="button-account-created-continue"
                    role="link"
                    href={personalDataUrl}
                    onClick={props.onClose}
                    variant={ButtonVariant.PRIMARY}
                    className="w-full"
                >
                    {t_continue}
                </AreaButton>
                {props.email && (
                    <FormattedText
                        rawText={t_passwordSent}
                        textColor="text-s text-p6-grey-dark text-center [&_b]:font-semibold"
                    />
                )}
            </div>
        </AnonymousFlowPopupFrame>
    );
};

export default AccountCreatedPopup;
